/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type ReactElement } from 'react';
import type { ShopperProducts } from '@/scapi';
import ImageGallery from '@/components/image-gallery';
import { WishlistButton } from '@/components/buttons/wishlist-button';
import ProductInfo from './product-info';
import ProductCartActions from '@/components/product-cart-actions';
import ProductViewProvider from '@/providers/product-view';
import { useProductImages } from '@/hooks/product/use-product-images';
import { useSelectedVariations } from '@/hooks/product/use-selected-variations';
import { isProductSet, isProductBundle } from '@/lib/product/product-utils';
import CollapsibleHtmlSection from '@/components/collapsible-section/collapsible-html-section';
import { useTranslation } from 'react-i18next';
import { UITarget } from '@/targets/ui-target';
import { resolveAssetUrl } from '@/lib/utils';

import type { SizeGuide } from './size-chart/size-guide-types';

interface ProductViewProps {
    product: ShopperProducts.schemas['Product'];
    mode?: 'add' | 'edit';
    category?: Promise<any>;
    sizeGuide?: SizeGuide | null;
}

/**
 * ProductView component renders a complete product detail view with image gallery and product information.
 *
 * @param props - The component props
 * @param props.product - The product data from Salesforce Commerce Cloud containing all product details,
 *                        variants, pricing, and metadata
 *
 * @returns A React element containing the complete product view layout
 *
 * @example
 * ```tsx
 * <ProductView product={productData} />
 * ```
 */
export default function ProductView({ product, category, sizeGuide }: ProductViewProps): ReactElement {


    // Calculate directly without useMemo since these are simple operations
    const isProductASet = isProductSet(product);
    const isProductABundle = isProductBundle(product);

    // Get selected attributes from URL parameters for image gallery
    const selectedAttributes = useSelectedVariations({ product });
    const { galleryImages } = useProductImages({
        product,
        selectedAttributes,
    });

    const { t } = useTranslation('product');

    return (
        <ProductViewProvider product={product} mode="add">
<div className="grid grid-cols-1 gap-4 lg:grid-cols-[588px_minmax(0,_1fr)] lg:gap-8 pdp-product-layout mb-0">
                {/* Left Column - Image Gallery + Description */}
                <div className="order-1">
                    <ImageGallery
                        key={product.id}
                        images={galleryImages}
                        eager={!isProductASet && !isProductABundle}
                        showNavigationArrows
                        navigationArrowSize="lg"
                        showThumbnails={true}
                        horizontalThumbnails={true}
                        productName={product.name}
                        showDots={true}
                        imageAction={
                            <WishlistButton
                                product={{
                                    productId: product.id,
                                    productName: product.name,
                                    price: product.price,
                                    image: product.imageGroups?.[0]?.images?.[0],
                                }}
                                surface="pdp"
                                size="sm"
                                className="bg-white hover:bg-white shadow-sm rounded-full border-0"
                            />
                        }
                    />
                    {/* Size Guide link is now placed inside SwatchGroup under size options */}
                    {/* CollapsibleHtmlSection for product description on mobile */}

                    <UITarget targetId="sfcc.pdp.agent.productHelper" />

                </div>
           
                {/* Right Column - Product Info */}
                <div className="order-2">
                    <ProductInfo product={product} category={category} sizeGuide={sizeGuide ?? undefined} />
                    <ProductCartActions product={product} />

                    {/* Mobile Features Strip — sits after pincode on mobile only */}
                    <div className="lg:hidden">
                        <FeaturesStrip />
                    </div>

                    {/* <UITarget targetId="sfcc.pdp.returnsWarranty" /> */}
                    {/* @sfdc-extension-block-start SFDC_EXT_SHIPPING_DELIVERY */}
                    {/* <UITarget targetId="sfcc.pdp.estimatedDelivery" /> */}
                    {/* @sfdc-extension-block-end SFDC_EXT_SHIPPING_DELIVERY */}
                    <UITarget targetId="sfcc.pdp.faq" />
                    <div className="border-t border-[#DDDDDD] mt-6 mb-0" />
                    {product.longDescription && product.longDescription !== product.shortDescription && (
                        <CollapsibleHtmlSection
                            label={`${t('Product Description')}`}
                            content={product.longDescription}
                            contentType="bulleted-list"
                            defaultOpen
                            className="pdp-collapsible-section"
                        />
                    )}
                    {((product as any).additionalDetailsOfProduct || (product as any).c_additionalDetailsOfProduct) && (
                        <CollapsibleHtmlSection
                            label={`${t('Product Features', 'Product Features')}`}
                            content={formatProductDetails(
                                (product as any).additionalDetailsOfProduct ||
                                (product as any).c_additionalDetailsOfProduct
                            )}
                            // contentType="bulleted-list"
                            className="pdp-collapsible-section"
                        />
                    )}
                    {((product as any).specifications || (product as any).c_specifications) && (
                        <CollapsibleHtmlSection
                            label={`${t('Product Specifications', 'Product Specifications')}`}
                            content={formatProductDetails(
                                (product as any).specifications ||
                                (product as any).c_specifications
                            )}
                            // contentType="bulleted-list"
                            className="pdp-collapsible-section"
                        />
                    )}
                    {((product as any).benefitsOfProduct || (product as any).c_benefitsOfProduct) && (
                        <CollapsibleHtmlSection
                            label={`${t('Additional Details', 'Additional Details')}`}
                            content={formatProductDetails(
                                (product as any).benefitsOfProduct || (product as any).c_benefitsOfProduct
                            )}
                            // contentType="bulleted-list"
                            className="pdp-collapsible-section"
                        />
                    )}
                    {((product as any).moreAboutThisItem || (product as any).c_moreAboutThisItem) && (
                        <CollapsibleHtmlSection
                            label={`${t('More About This Item', 'More About This Item')}`}
                            content={formatProductDetails(
                                (product as any).moreAboutThisItem || (product as any).c_moreAboutThisItem
                            )}
                            // contentType="bulleted-list"
                            className="pdp-collapsible-section"
                        />
                    )}

                    {/* <UITarget targetId="sfcc.pdp.collapsibles" /> */}
                </div>
            </div>

            {/* Desktop Features Strip — full width below the entire product layout & collapsibles */}
            <div className="hidden lg:block">
                <FeaturesStrip />
            </div>
        </ProductViewProvider>
    );
}

function FeaturesStrip(): ReactElement {
    return (
        <div className="pdp-features-strip">
            <div className="pdp-features-strip-item">
                <div className="pdp-features-strip-icon">
                    <img
                        src={resolveAssetUrl('/images/security-check.png')}
                        alt="Premium Quality Assured"
                        className="w-8 h-8 object-contain"
                        loading="lazy"
                    />
                </div>
                <h3 className="pdp-features-strip-title">Premium Quality Assured</h3>
                <p className="pdp-features-strip-desc">Superior material and craftsmanship for long-lasting use</p>
            </div>
            <div className="pdp-features-strip-item">
                <div className="pdp-features-strip-icon">
                    <img
                        src={resolveAssetUrl('/images/checkmark-badge.png')}
                        alt="Genuine Product"
                        className="w-8 h-8 object-contain"
                        loading="lazy"
                    />
                </div>
                <h3 className="pdp-features-strip-title">Genuine Product</h3>
                <p className="pdp-features-strip-desc">Original product that reflect true essence of Mahindra</p>
            </div>
            <div className="pdp-features-strip-item">
                <div className="pdp-features-strip-icon">
                    <img
                        src={resolveAssetUrl('/images/refresh.png')}
                        alt="Easy Return"
                        className="w-8 h-8 object-contain"
                        loading="lazy"
                    />
                </div>
                <h3 className="pdp-features-strip-title">Easy Return</h3>
                <p className="pdp-features-strip-desc">Hassle-free return and exchanges within the return window</p>
            </div>
            <div className="pdp-features-strip-item">
                <div className="pdp-features-strip-icon">
                    <img
                        src={resolveAssetUrl('/images/square-lock.png')}
                        alt="Secure Payment"
                        className="w-8 h-8 object-contain"
                        loading="lazy"
                    />
                </div>
                <h3 className="pdp-features-strip-title">Secure Payment</h3>
                <p className="pdp-features-strip-desc">100% secure payments with trusted and encrypted gateway</p>
            </div>
        </div>
    );
}

function formatProductDetails(content: any): string {
    if (!content) return '';

    if (Array.isArray(content)) {
        const hasPipe = content.some((item) => typeof item === 'string' && item.includes('|'));
        const renderedItems = content
            .map((item) => {
                const str = (typeof item === 'string' ? item : String(item)).trim();
                if (!str) return '';
                if (str.includes('|')) {
                    const parts = str.split('|');
                    const label = parts[0].trim();
                    const value = parts.slice(1).join('|').trim();
                    return `<div class="pdp-feature-item"><span class="pdp-feature-label">${label}</span><span class="pdp-feature-value">${value}</span></div>`;
                }
                return `<li>${str}</li>`;
            })
            .filter(Boolean)
            .join('\n');

        return hasPipe
            ? `<div class="pdp-feature-grid">\n${renderedItems}\n</div>`
            : `<ul class="list-disc pl-5 space-y-1">\n${renderedItems}\n</ul>`;
    }

    // Safely get string content
    let strContent = '';
    if (typeof content === 'string') {
        strContent = content;
    } else if (typeof content === 'object') {
        strContent = content.source || content.markup || content.default || content.value || '';
        if (!strContent) {
            strContent = String(content);
        }
    } else {
        strContent = String(content);
    }

    if (typeof strContent !== 'string') {
        strContent = String(strContent);
    }

    // Strip HTML tags to see if the raw text contains the pipe format
    const rawText = strContent.replace(/<\/?[^>]+(>|$)/g, '').trim();
    if (!rawText.includes('|')) {
        return strContent;
    }

    const items = rawText.split(',');
    const renderedItems = items
        .map((item) => {
            const trimmedItem = item.trim();
            if (!trimmedItem) return '';

            if (trimmedItem.includes('|')) {
                const parts = trimmedItem.split('|');
                const label = parts[0].trim();
                const value = parts.slice(1).join('|').trim();

                return `
<div class="pdp-feature-item">
    <span class="pdp-feature-label">${label}</span>
    <span class="pdp-feature-value">${value}</span>
</div>`.trim();
            }
            return `<div class="pdp-feature-item pdp-feature-value">${trimmedItem}</div>`;
        })
        .filter(Boolean)
        .join('\n');

    return `<div class="pdp-feature-grid">\n${renderedItems}\n</div>`;
}

